/**
 * StatusDisplay - Live verification status panel with score calculation breakdown
 *
 * Shows real-time proctoring metrics:
 * - Last verification result (faces, similarity)
 * - Consecutive failure count (strike counter)
 * - Current proctoring score with full calculation breakdown
 * - Pass/fail status
 */

import { useProctoringConfigStore } from '../../store/proctoringConfigStore'

interface StatusDisplayProps {
  lastResult: {
    timestamp: number
    facesDetected: number
    similarity: number
    verified: boolean
    error?: string
  } | null
  consecutiveFailures: number
  maxFailures: number
  similarityThreshold: number
  currentScore: number
  passingScore: number
  isVerifying: boolean
  isSuspended: boolean
  violationCounts: {
    noFace: number
    multipleFaces: number
    identityMismatch: number
    tabSwitch: number
  }
}

export default function StatusDisplay({
  lastResult,
  consecutiveFailures,
  maxFailures,
  similarityThreshold,
  currentScore,
  passingScore,
  isVerifying,
  isSuspended,
  violationCounts,
}: StatusDisplayProps) {
  const config = useProctoringConfigStore()
  const isPassing = currentScore >= passingScore
  const hasStarted = lastResult !== null

  // Calculate individual component scores for display
  const identityScore = lastResult?.similarity || 0
  const presenceScore = lastResult?.facesDetected === 1 ? 100 : 0

  // Calculate behavior penalty
  const behaviorPenalty =
    (violationCounts.noFace * config.violationPenalties.noFace) +
    (violationCounts.multipleFaces * config.violationPenalties.multipleFaces) +
    (violationCounts.identityMismatch * config.violationPenalties.identityMismatch) +
    (violationCounts.tabSwitch * config.violationPenalties.tabSwitch)
  const behaviorScore = Math.max(0, 100 - behaviorPenalty)

  // Calculate weighted contributions
  const identityContribution = identityScore * config.scoringWeights.identity
  const presenceContribution = presenceScore * config.scoringWeights.presence
  const behaviorContribution = behaviorScore * config.scoringWeights.behavior
  const rawTotal = identityContribution + presenceContribution + behaviorContribution
  const calculatedTotal = Math.min(100, Math.max(0, Math.round(rawTotal))) // Clamp to 0-100

  // Check if weights sum to ~100%
  const weightsSum = config.scoringWeights.identity + config.scoringWeights.presence + config.scoringWeights.behavior
  const weightsInvalid = Math.abs(weightsSum - 1) > 0.01

  return (
    <div className="bg-gray-800 rounded-xl p-4 space-y-4">
      <h2 className="text-lg font-semibold">Live Status</h2>

      {/* Score Display with Calculation Breakdown - only show after verification has started */}
      {hasStarted && (
        <div className="p-4 bg-gray-900 rounded-lg space-y-3">
          {/* Final Score */}
          <div className="text-center">
            <div className="text-sm text-gray-400 mb-1">Proctoring Score</div>
            <div className={`text-4xl font-bold ${
              isSuspended ? 'text-red-500' :
              isPassing ? 'text-green-400' : 'text-yellow-400'
            }`}>
              {calculatedTotal}%
            </div>
            <div className={`text-sm mt-1 ${isPassing ? 'text-green-400' : 'text-yellow-400'}`}>
              {isSuspended ? 'SUSPENDED' : isPassing ? 'Passing' : `Need ${passingScore}% to pass`}
            </div>
          </div>

          {/* Warning if weights don't sum to 100% */}
          {weightsInvalid && (
            <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-2 text-xs text-yellow-400">
              ⚠️ Weights sum to {(weightsSum * 100).toFixed(0)}% (should be 100%). Score capped at 0-100%.
            </div>
          )}

          {/* Calculation Breakdown */}
          <div className="border-t border-gray-700 pt-3 space-y-2">
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Score Calculation</div>

            {/* Identity Component */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Identity</span>
              <span className="font-mono text-blue-400">
                {Math.round(identityScore)} × {(config.scoringWeights.identity * 100).toFixed(0)}% = <span className="text-white">{identityContribution.toFixed(1)}</span>
              </span>
            </div>

            {/* Presence Component */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Presence</span>
              <span className="font-mono text-blue-400">
                {presenceScore} × {(config.scoringWeights.presence * 100).toFixed(0)}% = <span className="text-white">{presenceContribution.toFixed(1)}</span>
              </span>
            </div>

            {/* Behavior Component */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Behavior</span>
              <span className="font-mono text-blue-400">
                {behaviorScore} × {(config.scoringWeights.behavior * 100).toFixed(0)}% = <span className="text-white">{behaviorContribution.toFixed(1)}</span>
              </span>
            </div>

            {/* Sum Line */}
            <div className="flex items-center justify-between text-sm border-t border-gray-700 pt-2 mt-2">
              <span className="text-gray-300 font-medium">Total</span>
              <span className="font-mono text-white font-bold">
                {identityContribution.toFixed(1)} + {presenceContribution.toFixed(1)} + {behaviorContribution.toFixed(1)} = {calculatedTotal}%
              </span>
            </div>
          </div>

          {/* Behavior Penalty Breakdown (if any violations) */}
          {behaviorPenalty > 0 && (
            <div className="border-t border-gray-700 pt-3">
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Behavior Penalties</div>
              <div className="text-xs space-y-1 font-mono">
                {violationCounts.noFace > 0 && (
                  <div className="flex justify-between text-red-400">
                    <span>No Face × {violationCounts.noFace}</span>
                    <span>-{violationCounts.noFace * config.violationPenalties.noFace} pts</span>
                  </div>
                )}
                {violationCounts.multipleFaces > 0 && (
                  <div className="flex justify-between text-red-400">
                    <span>Multiple Faces × {violationCounts.multipleFaces}</span>
                    <span>-{violationCounts.multipleFaces * config.violationPenalties.multipleFaces} pts</span>
                  </div>
                )}
                {violationCounts.identityMismatch > 0 && (
                  <div className="flex justify-between text-red-400">
                    <span>Identity Mismatch × {violationCounts.identityMismatch}</span>
                    <span>-{violationCounts.identityMismatch * config.violationPenalties.identityMismatch} pts</span>
                  </div>
                )}
                {violationCounts.tabSwitch > 0 && (
                  <div className="flex justify-between text-red-400">
                    <span>Tab Switch × {violationCounts.tabSwitch}</span>
                    <span>-{violationCounts.tabSwitch * config.violationPenalties.tabSwitch} pts</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-300 border-t border-gray-700 pt-1 mt-1">
                  <span>Behavior Score</span>
                  <span>100 - {behaviorPenalty} = {behaviorScore}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Strike Counter - only show after verification has started */}
      {hasStarted && (
        <div className="flex items-center justify-between p-3 bg-gray-900 rounded-lg">
          <span className="text-gray-400">Consecutive Failures</span>
          <div className="flex items-center gap-2">
            {Array.from({ length: maxFailures }).map((_, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full transition-colors ${
                  i < consecutiveFailures ? 'bg-red-500' : 'bg-gray-600'
                }`}
              />
            ))}
            <span className="ml-2 text-sm font-mono">
              {consecutiveFailures}/{maxFailures}
            </span>
          </div>
        </div>
      )}

      {/* Last Verification Result */}
      {lastResult && (
        <div className="space-y-2">
          <div className="text-sm text-gray-400">Last Verification</div>

          <div className="grid grid-cols-2 gap-2">
            {/* Faces Detected */}
            <div className={`p-3 rounded-lg ${
              lastResult.facesDetected === 1 ? 'bg-green-900/30' :
              lastResult.facesDetected === 0 ? 'bg-yellow-900/30' :
              'bg-red-900/30'
            }`}>
              <div className="text-xs text-gray-400">Faces</div>
              <div className={`text-xl font-bold ${
                lastResult.facesDetected === 1 ? 'text-green-400' :
                lastResult.facesDetected === 0 ? 'text-yellow-400' :
                'text-red-400'
              }`}>
                {lastResult.facesDetected}
              </div>
            </div>

            {/* Similarity Score */}
            <div className={`p-3 rounded-lg ${
              lastResult.similarity >= similarityThreshold ? 'bg-green-900/30' :
              lastResult.similarity > 0 ? 'bg-yellow-900/30' :
              'bg-gray-900'
            }`}>
              <div className="text-xs text-gray-400">Similarity</div>
              <div className={`text-xl font-bold ${
                lastResult.similarity >= similarityThreshold ? 'text-green-400' :
                lastResult.similarity > 0 ? 'text-yellow-400' :
                'text-gray-500'
              }`}>
                {lastResult.similarity > 0 ? `${Math.round(lastResult.similarity)}%` : '-'}
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div className={`p-3 rounded-lg text-center ${
            lastResult.verified ? 'bg-green-900/30' :
            lastResult.error ? 'bg-red-900/30' :
            'bg-yellow-900/30'
          }`}>
            <span className={`font-medium ${
              lastResult.verified ? 'text-green-400' :
              lastResult.error ? 'text-red-400' :
              'text-yellow-400'
            }`}>
              {lastResult.verified ? 'Identity Verified' :
               lastResult.error ? `Error: ${lastResult.error}` :
               'Identity Not Verified'}
            </span>
          </div>

          {/* Timestamp */}
          <div className="text-xs text-gray-500 text-right">
            {new Date(lastResult.timestamp).toLocaleTimeString()}
          </div>
        </div>
      )}

      {/* Waiting State */}
      {!lastResult && isVerifying && (
        <div className="p-4 bg-gray-900 rounded-lg text-center text-gray-400">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          Waiting for first verification...
        </div>
      )}

      {/* Idle State */}
      {!lastResult && !isVerifying && (
        <div className="p-4 bg-gray-900 rounded-lg text-center text-gray-500">
          Enroll a face and start verification to see results
        </div>
      )}
    </div>
  )
}
